/**
 * Card resumido de um equipment — usado no Dashboard e na lista
 * de geladeiras.
 */

import { Link } from "react-router-dom";
import {
  Building2,
  DoorClosed,
  DoorOpen,
  Droplets,
  MapPin,
  Plug,
  Snowflake,
  Thermometer,
} from "lucide-react";
import clsx from "clsx";

import type { EquipmentSummary, HealthGrade, Severity } from "@/types";
import { Card } from "./Card";
import { Badge } from "./Badge";
import { DeviceHealthBadges } from "./DeviceHealthBadges";
import { useEquipmentInsight } from "@/hooks/queries";
import {
  formatPower,
  formatTemperature,
  timeAgo,
  toNumber,
} from "@/lib/format";
import {
  evaluateTemperature,
  statusLabel,
  statusTextColor,
  statusTone,
  type SetpointConfig,
} from "@/lib/thresholds";

interface Props {
  summary: EquipmentSummary;
  /** Link de destino ao clicar — default `/equipments/{id}`. */
  to?: string;
  /**
   * Severity do alerta firing pra este ativo — pinta o card.
   * Use 'critical' (vermelho leve) ou 'warning' (amarelo leve).
   * Caller deve resolver isso a partir de useActiveAlerts().
   */
  alertSeverity?: Severity | null;
}

const ALERT_TINT: Record<Severity, string> = {
  // ! prefix força sobrescrever o bg-white dark:bg-slate-900 do <Card>.
  // Em dark, usa tom muito escuro (-950/40) + ring um pouco mais saturado
  // pra manter a identificação visual sem perder contraste de leitura.
  info:
    "!bg-sky-50 ring-1 ring-sky-200/70 " +
    "dark:!bg-sky-950/40 dark:ring-sky-700/40",
  warning:
    "!bg-amber-50 ring-1 ring-amber-200/70 " +
    "dark:!bg-amber-950/40 dark:ring-amber-700/40",
  critical:
    "!bg-red-50 ring-1 ring-red-300/80 " +
    "dark:!bg-red-950/50 dark:ring-red-700/50",
  // `disaster` não pode dividir o vermelho com `critical`: num painel cheio
  // de cards, a diferença entre "algo está errado" e "a carga vai estragar"
  // precisa ser visível antes de alguém ler o texto.
  disaster:
    "!bg-fuchsia-50 ring-2 ring-fuchsia-400/80 " +
    "dark:!bg-fuchsia-950/50 dark:ring-fuchsia-600/60",
};

/**
 * Cores do badge de Health Score. Reusa a mesma paleta do envpulse AI.
 * Score numérico discreto colorido no canto superior direito do card.
 */
const HEALTH_BADGE: Record<HealthGrade, string> = {
  excellent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  good:      "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
  fair:      "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  poor:      "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

export function EquipmentCard({ summary, to, alertSeverity }: Props) {
  const cfg = (summary.metadata ?? {}) as SetpointConfig;

  // envpulse AI score discreto. Hook tem retry: false e staleTime 5min,
  // então 404 (equipamento sem insight ainda) é silent — só não renderiza.
  // Não fetch insight de ativo arquivado — backend retorna 404 e polui logs
  const insight = useEquipmentInsight(summary.equipment_id, !summary.archived);

  // Temperatura primária = interna se existir, senão produto.
  const primaryTemp =
    summary.metrics.temp_internal ??
    summary.metrics.temp_product ??
    summary.metrics.INTERNAL_temperature ??
    summary.metrics.PRODUCT_temperature ??
    null;

  const productTemp =
    summary.metrics.temp_product ??
    summary.metrics.PRODUCT_temperature ??
    null;

  const externalTemp =
    summary.metrics.temp_external ??
    summary.metrics.EXTERNAL_temperature ??
    null;

  const power =
    summary.metrics.power ??
    summary.metrics.ENERGY_power ??
    null;

  // Umidade — mesma resolução do EquipmentDetail (ordem de prioridade):
  // device dedicado → junto com temp interna → Tasmota all-in-one.
  const humidity =
    summary.metrics.humidity_internal ??
    summary.metrics.temp_internal_humidity ??
    summary.metrics.INTERNAL_humidity ??
    summary.metrics.humidity ??
    null;

  // Door: 1 = aberta, 0 = fechada (Tasmota DOOR section).
  // Preserva null quando não há metric — assim o chip não renderiza pra
  // equipment sem sensor de porta (ou se device de porta é inativo/stale).
  const doorVal =
    summary.metrics.door ??
    summary.metrics.DOOR_contact ??
    null;
  const doorNum = toNumber(doorVal);
  const hasDoor = doorNum !== null;
  const doorOpen = doorNum === 1;

  const eval_ = evaluateTemperature(primaryTemp, cfg, summary.last_seen);
  const tone = statusTone(eval_.status);

  const archived = !!summary.archived;

  return (
    <Link to={to ?? `/equipments/${summary.equipment_id}`} className="block">
      <Card
        className={clsx(
          "h-full transition-shadow hover:shadow-card-hover",
          alertSeverity && ALERT_TINT[alertSeverity],
          archived && "opacity-60",  // visual "morto" pra ativos arquivados
        )}
      >
        {/* Crumbs (linha fina): Tenant · Site */}
        {(summary.tenant_name || summary.site_name) && (
          <div className="flex items-center gap-2 border-b border-surface-100 dark:border-slate-800 px-5 py-1.5 text-[11px] text-slate-500 dark:text-slate-400">
            {summary.tenant_name && (
              <span className="flex items-center gap-1 truncate">
                <Building2 className="h-3 w-3 flex-none text-slate-400 dark:text-slate-500" />
                <span className="truncate">{summary.tenant_name}</span>
              </span>
            )}
            {summary.tenant_name && summary.site_name && (
              <span className="text-slate-300">·</span>
            )}
            {summary.site_name && (
              <span className="flex items-center gap-1 truncate">
                <MapPin className="h-3 w-3 flex-none text-slate-400 dark:text-slate-500" />
                <span className="truncate">{summary.site_name}</span>
              </span>
            )}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-100 dark:border-slate-800 px-5 py-3">
          <div className="flex items-center gap-2">
            <Snowflake className="h-4 w-4 text-brand-600" />
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {summary.name}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {archived ? (
              <Badge tone="neutral">Arquivada</Badge>
            ) : (
              <>
                {insight.data && (
                  <span
                    className={clsx(
                      "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                      HEALTH_BADGE[insight.data.health_grade],
                    )}
                    title={`envpulse AI · ${insight.data.health_grade}`}
                  >
                    <span className="text-[8px]">✦</span>
                    {insight.data.health_score}
                  </span>
                )}
                <Badge tone={tone}>{statusLabel(eval_.status)}</Badge>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <div className="flex items-baseline gap-2">
            <Thermometer className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            <span
              className={clsx(
                "text-3xl font-bold tabular-nums",
                statusTextColor(eval_.status),
              )}
            >
              {formatTemperature(primaryTemp)}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {productTemp !== null && (
              <>produto: {formatTemperature(productTemp)} · </>
            )}
            {externalTemp !== null && (
              <>externa: {formatTemperature(externalTemp)}</>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
            {hasDoor && (
              <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                {doorOpen ? (
                  <DoorOpen className="h-4 w-4 text-amber-600" />
                ) : (
                  <DoorClosed className="h-4 w-4 text-emerald-600" />
                )}
                <span className={doorOpen ? "text-amber-700" : "text-slate-600 dark:text-slate-400"}>
                  {doorOpen ? "Aberta" : "Fechada"}
                </span>
              </div>
            )}
            {humidity !== null && (
              <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                <Droplets className="h-4 w-4 text-sky-500" />
                <span className="tabular-nums">
                  {toNumber(humidity)?.toFixed(0) ?? "—"}%
                </span>
              </div>
            )}
            {power !== null && (
              <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                <Plug className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                {formatPower(power)}
              </div>
            )}
          </div>
        </div>

        {/* Footer — health icons (bateria + sinal) por device */}
        <div className="flex items-center justify-between gap-3 border-t border-surface-100 dark:border-slate-800 px-5 py-2.5 text-xs text-slate-500 dark:text-slate-400">
          <span className="whitespace-nowrap">{timeAgo(summary.last_seen)}</span>
          <div className="flex-shrink-0">
            <DeviceHealthBadges
              items={summary.devices.map((d) => ({
                device: d,
                status: summary.device_statuses[d.id] ?? null,
              }))}
            />
          </div>
        </div>
      </Card>
    </Link>
  );
}
